/**
 * Developer Dashboard Home - 데이터 통계 대시보드
 */

import { Trans } from '@lingui/macro';
import {
    Database, Building2, MessageSquare, Users, Bell,
    FileText, HardDrive, RefreshCw, Loader2, AlertTriangle,
    TrendingUp, Clock
} from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Fire, getFunc } from '../../services/FireClient';
import { collection, getDocs, collectionGroup, query, limit, orderBy } from 'firebase/firestore';

interface CollectionStat {
    name: string;
    count: number;
    icon: React.ElementType;
    color: string;
}

interface DashboardStats {
    collections: CollectionStat[];
    loading: boolean;
    lastUpdated: Date | null;
}

const DevAdminDashboard: React.FC = () => {
    const [stats, setStats] = useState<DashboardStats>({
        collections: [],
        loading: true,
        lastUpdated: null,
    });
    const [refreshing, setRefreshing] = useState(false);

    useEffect(() => {
        loadStats();
    }, []);

    const loadStats = async () => {
        setRefreshing(true);
        try {
            const store = Fire.store;

            // 각 컬렉션의 문서 수 조회 (getCountFromServer 사용 - 컬렉션당 1 read)
            const collectionsToCheck = [
                { name: 'workspaces', icon: Building2, color: 'bg-blue-500' },
                { name: 'rooms', icon: MessageSquare, color: 'bg-green-500' },
                { name: 'users', icon: Users, color: 'bg-orange-500' },
                { name: 'roominvites', icon: FileText, color: 'bg-pink-500' },
            ];

            // Note: messages, notifications는 규모가 커서 count 쿼리도 비용이 높을 수 있음
            // 실제 프로덕션에서는 캐싱된 통계 사용 권장

            const results: CollectionStat[] = [];

            for (const col of collectionsToCheck) {
                try {
                    // getCountFromServer: 문서 수와 관계없이 1 read만 소비
                    const { getCountFromServer } = await import('firebase/firestore');
                    const countSnap = await getCountFromServer(collection(store, col.name));
                    results.push({
                        name: col.name,
                        count: countSnap.data().count,
                        icon: col.icon,
                        color: col.color,
                    });
                } catch (error) {
                    console.error(`Failed to count ${col.name}:`, error);
                    results.push({
                        name: col.name,
                        count: -1, // 에러 표시
                        icon: col.icon,
                        color: col.color,
                    });
                }
            }

            setStats({
                collections: results,
                loading: false,
                lastUpdated: new Date(),
            });
        } catch (error) {
            console.error('Failed to load stats:', error);
        } finally {
            setRefreshing(false);
        }
    };

    const formatNumber = (num: number) => {
        if (num < 0) return 'Error';
        if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
        if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
        return num.toString();
    };

    return (
        <div className="flex flex-col h-full">
            {/* Header */}
            <div className="bg-white/80 backdrop-blur-md border-b border-slate-100/50 px-6 py-4">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-xl font-bold text-slate-900">
                            <Trans>Data Dashboard</Trans>
                        </h1>
                        <p className="text-sm text-slate-500 mt-0.5">
                            <Trans>Firestore collections overview and statistics</Trans>
                        </p>
                    </div>
                    <div className="flex items-center gap-3">
                        {stats.lastUpdated && (
                            <span className="text-xs text-slate-400">
                                Updated: {stats.lastUpdated.toLocaleTimeString()}
                            </span>
                        )}
                        <Button
                            onClick={loadStats}
                            disabled={refreshing}
                            variant="outline"
                            size="sm"
                        >
                            {refreshing ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                                <RefreshCw className="w-4 h-4" />
                            )}
                            <span className="ml-2"><Trans>Refresh</Trans></span>
                        </Button>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-auto p-6 bg-slate-50/30">
                {stats.loading ? (
                    <div className="flex items-center justify-center py-20">
                        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
                    </div>
                ) : (
                    <div className="space-y-6">
                        {/* Collection Stats Grid */}
                        <div>
                            <h2 className="text-lg font-semibold text-slate-800 mb-4">
                                <Trans>Collections</Trans>
                            </h2>
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                                {stats.collections.map((col) => {
                                    const Icon = col.icon;
                                    return (
                                        <Card key={col.name} className="hover:shadow-md transition-shadow">
                                            <CardContent className="p-4">
                                                <div className="flex items-center gap-3 mb-2">
                                                    <div className={`w-10 h-10 rounded-lg ${col.color} flex items-center justify-center`}>
                                                        <Icon className="w-5 h-5 text-white" />
                                                    </div>
                                                </div>
                                                <div className="text-2xl font-bold text-slate-900">
                                                    {formatNumber(col.count)}
                                                </div>
                                                <div className="text-sm text-slate-500 capitalize">
                                                    {col.name}
                                                </div>
                                            </CardContent>
                                        </Card>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Quick Actions */}
                        <div>
                            <h2 className="text-lg font-semibold text-slate-800 mb-4">
                                <Trans>Quick Actions</Trans>
                            </h2>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <Card className="cursor-pointer hover:shadow-md transition-shadow">
                                    <CardContent className="p-4 flex items-center gap-4">
                                        <div className="w-12 h-12 rounded-lg bg-amber-100 flex items-center justify-center">
                                            <AlertTriangle className="w-6 h-6 text-amber-600" />
                                        </div>
                                        <div>
                                            <div className="font-medium text-slate-900">
                                                <Trans>Check Data Integrity</Trans>
                                            </div>
                                            <div className="text-sm text-slate-500">
                                                <Trans>Verify storage vs stats</Trans>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>

                                <Card className="cursor-pointer hover:shadow-md transition-shadow">
                                    <CardContent className="p-4 flex items-center gap-4">
                                        <div className="w-12 h-12 rounded-lg bg-red-100 flex items-center justify-center">
                                            <Clock className="w-6 h-6 text-red-600" />
                                        </div>
                                        <div>
                                            <div className="font-medium text-slate-900">
                                                <Trans>Find Cold Data</Trans>
                                            </div>
                                            <div className="text-sm text-slate-500">
                                                <Trans>180+ days inactive</Trans>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>

                                <Card className="cursor-pointer hover:shadow-md transition-shadow">
                                    <CardContent className="p-4 flex items-center gap-4">
                                        <div className="w-12 h-12 rounded-lg bg-emerald-100 flex items-center justify-center">
                                            <TrendingUp className="w-6 h-6 text-emerald-600" />
                                        </div>
                                        <div>
                                            <div className="font-medium text-slate-900">
                                                <Trans>Usage Report</Trans>
                                            </div>
                                            <div className="text-sm text-slate-500">
                                                <Trans>Storage & bandwidth</Trans>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default DevAdminDashboard;
